import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import { IMAGES, VALUES, TIMELINE, COMPANY, LEADERSHIP } from "@/data/site";
import { Reveal, Stagger, StaggerItem } from "@/components/Motion";
import { PageHero, CTABand, SectionHeading, Overline } from "@/components/UI";
import SEO from "@/components/SEO";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

export default function About() {
  return (
    <>
      <SEO
        title="About"
        description="Consilium Pro is a Principal-led consulting and fractional services firm built by experienced practitioners across business, government, military, security, technology, training, communications, and operations."
        path="/about"
        image={IMAGES.diverseTeam}
      />
      <PageHero
        eyebrow="About Consilium Pro"
        title={["Principal-led.", "Multidisciplinary.", "Practitioner-driven."]}
        subtitle="We are a consulting and fractional services firm headquartered in Atlanta, Georgia, built by practitioners who have led operations, people, technology, communications, security, logistics, and mission-critical work from the inside."
        image={IMAGES.diverseTeam}
      />

      {/* Etymology, What Consilium Pro means */}
      <section className="bg-navy py-20 md:py-28 relative overflow-hidden" data-testid="about-mission">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 relative">
          <Overline className="text-gold">Our Mission</Overline>
          <p className="mt-6 font-display font-700 text-white text-xl md:text-2xl lg:text-[1.7rem] leading-snug tracking-tight" data-testid="mission-statement">
            Our mission is to help small and growing businesses, especially those in underserved communities, access the executive-level expertise they need to build strong, sustainable organizations. Through fractional consulting in HR, technology, marketing, communications, operations, and strategy, Consilium Pro gives business owners the support, structure, and guidance of an experienced leadership team at a fraction of the traditional cost. <span className="text-gold">We exist to help good businesses grow, compete and succeed without being limited by the resources they could not afford at the start.</span>
          </p>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="about-etymology">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="What the Name Means" title={<>The meaning of <span className="text-gold">Consilium Pro</span></>} intro="Two words that describe how we work, professional counsel translated into practical action." />
          <div className="mt-14 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-10 md:p-12" data-testid="about-etymology-consilium">
              <span className="overline text-gold">Consilium</span>
              <p className="mt-4 font-display font-800 text-navy tracking-tightest text-4xl leading-none">Consilium</p>
              <p className="mt-4 text-navy/60 text-sm uppercase tracking-wider">Latin origin</p>
              <p className="mt-6 text-navy/85 leading-relaxed">Counsel, deliberation, and wise advice. A meeting of judgment brought to bear on decisions that matter.</p>
            </div>
            <div className="bg-lightgray p-10 md:p-12" data-testid="about-etymology-pro">
              <span className="overline text-gold">Pro</span>
              <p className="mt-4 font-display font-800 text-navy tracking-tightest text-4xl leading-none">Pro</p>
              <p className="mt-4 text-navy/60 text-sm uppercase tracking-wider">Professional · Forward · Executed</p>
              <p className="mt-6 text-navy/85 leading-relaxed">Professional, forward-moving, and execution-oriented. Counsel that gets built, not filed away.</p>
            </div>
          </div>
          <p className="mt-10 text-navy/75 leading-relaxed max-w-3xl">
            <span className="font-700 text-navy">Together:</span> Consilium Pro means <em>professional counsel translated into practical action</em>. Advice that becomes systems. Judgment that becomes execution. It's the through-line of how our Principals engage every organization we support.
          </p>
        </div>
      </section>

      {/* Principal-Led Model */}
      <section className="bg-navy py-24 md:py-32 relative overflow-hidden" data-testid="about-principal-led">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="How We Work" title="The Principal-Led Model" intro="Client work is guided by Principals, each responsible for a defined practice area, and supported by specialists assembled around the engagement." light />
          <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="about-principal-grid">
            {LEADERSHIP.map((p) => (
              <div key={p.name} className="bg-white/[0.04] border border-white/15 p-6 hover:border-gold transition-colors" data-testid={`about-principal-${p.initials.toLowerCase()}`}>
                <span className="text-[10px] font-700 uppercase tracking-wider text-gold">Principal</span>
                <h4 className="mt-2 font-display font-700 text-white leading-snug">{p.name}</h4>
                <p className="mt-1 text-white/60 text-xs">{p.role}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(p.expertise || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] text-white/85 border border-white/20 px-2 py-0.5">{t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
            <div className="border-l-2 border-gold pl-5">
              <h4 className="font-display font-700 text-white">Every engagement has a responsible Principal.</h4>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">The Principal is accountable for the outcome and the point of contact throughout the engagement. Work is never handed off to an unnamed team.</p>
            </div>
            <div className="border-l-2 border-gold pl-5">
              <h4 className="font-display font-700 text-white">Multidisciplinary teams by design.</h4>
              <p className="mt-2 text-white/70 text-sm leading-relaxed">When a challenge spans practices, we assemble the right combination of Principals and specialists around it. A firm that scales without diluting judgment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story + Mission/Vision */}
      <section className="bg-white py-24 md:py-32" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <SectionHeading eyebrow="Our Story" title="Built on practitioner experience" intro="Consilium Pro was formed by operators and advisors who understand that strategy is only as valuable as its execution. We bridge the gap between the boardroom and the front line, translating vision into workforce systems, technology, and leadership that deliver." />
            <p className="mt-6 text-gray-600 leading-relaxed">We support commercial businesses, nonprofit organizations, and government contractors with the discipline of a senior consulting firm and the accessibility of a dedicated partner.</p>
            <Link
              to="/our-story"
              data-testid="about-read-our-story"
              className="mt-8 inline-flex items-center gap-2 text-navy font-700 border-b-2 border-gold hover:text-gold transition-colors pb-1"
            >
              Read the full founding story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 lg:col-span-2">
            {[
              { t: "Vision", d: "To be the trusted strategic partner for organizations pursuing workforce transformation, technology modernization, and operational excellence.", icon: "Eye" },
              { t: "Why We Exist", d: "Because organizations that invest in their people, operations, and technology outperform, and every mission deserves that advantage.", icon: "Flame" },
              { t: "Leadership Philosophy", d: "Lead with integrity, decide with data, and measure success by outcomes that outlast the engagement.", icon: "Award" },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-lightgray border border-slate-200 p-8 h-full">
                  <Icon name={c.icon} className="w-6 h-6 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display font-700 text-navy text-lg">{c.t}</h3>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="bg-navy py-24 md:py-32 relative overflow-hidden" data-testid="about-values">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Core Values" title="The principles behind every engagement" light />
          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {VALUES.map((v, i) => (
              <StaggerItem key={i}>
                <div className="bg-navy p-8 h-full">
                  <span className="font-display font-800 text-5xl text-gold/30">0{i + 1}</span>
                  <h3 className="mt-4 font-display font-700 text-white text-lg">{v.label}</h3>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Our approach - manifesto chapters */}
      <section className="bg-white py-24 md:py-32" data-testid="about-approach">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Our Approach" title="How we deliver measurable results" />
          <div className="mt-16 space-y-px">
            {[
              { n: "01", t: "Understand", d: "We immerse in your mission, constraints, and culture before recommending a single change." },
              { n: "02", t: "Diagnose", d: "Data-driven assessment of workforce, technology, and operations to find the highest-leverage opportunities." },
              { n: "03", t: "Design", d: "Customized solutions engineered for your context, never off-the-shelf templates." },
              { n: "04", t: "Deliver", d: "Hands-on implementation with change management to ensure adoption and durable impact." },
              { n: "05", t: "Sustain", d: "Systems, training, and partnerships that keep results compounding long after we're done." },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="grid md:grid-cols-12 gap-6 items-baseline py-10 border-t border-slate-200 group">
                  <span className="md:col-span-2 font-display font-800 text-3xl text-slate-200 group-hover:text-gold transition-colors">{c.n}</span>
                  <h3 className="md:col-span-3 font-display font-700 text-2xl text-navy">{c.t}</h3>
                  <p className="md:col-span-7 text-gray-600 leading-relaxed max-w-2xl">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-lightgray py-24 md:py-32" data-testid="about-timeline">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Our Journey" title="From founding to future vision" />
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {TIMELINE.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white border border-slate-200 p-8 h-full border-t-2 border-t-gold">
                  <Overline>{t.year}</Overline>
                  <h3 className="mt-5 font-display font-700 text-navy text-xl">{t.title}</h3>
                  <p className="mt-3 text-gray-600 text-sm leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
